import { createApi } from '@reduxjs/toolkit/query/react';

import { ManagedUser } from '../types/IUserManagement';

import { axiosBaseQuery } from '@/redux/axios';
import { apiRoutes } from '@/api/apiRoutes';
import {
  ApplicationAllowedRolesResponse,
  UpdateUserPermissionsRequest,
} from '@/types/userPermissions';

interface UpdateUserPermissionsArgs {
  userId: string;
  body: UpdateUserPermissionsRequest;
}

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['UsersPermissions', 'ApplicationRoles'],
  endpoints: (builder) => ({
    getUsersPermissions: builder.query<ManagedUser[], void>({
      query: () => ({
        url: apiRoutes.getUsersAndPermissions.path(),
        method: apiRoutes.getUsersAndPermissions.method,
      }),
      providesTags: ['UsersPermissions'],
    }),
    getApplicationRoles: builder.query<ApplicationAllowedRolesResponse[], void>(
      {
        query: () => ({
          url: apiRoutes.getApplicationRoles.path(),
          method: apiRoutes.getApplicationRoles.method,
        }),
        providesTags: ['ApplicationRoles'],
      }
    ),
    // Deliberately no `invalidatesTags`. Both save paths refetch the list
    // themselves, exactly once: the modal after its single PUT, the batch panel
    // after the whole run. Tag invalidation would instead fire one refetch per
    // PUT, so a batch of N users would pull the list N times and race its own
    // fresh-read baseline.
    updateUserPermissions: builder.mutation<
      ManagedUser,
      UpdateUserPermissionsArgs
    >({
      query: ({ userId, body }) => ({
        url: apiRoutes.updateUserPermissionsById.path(userId),
        method: apiRoutes.updateUserPermissionsById.method,
        data: body,
      }),
    }),
  }),
});

export const {
  useGetUsersPermissionsQuery,
  useGetApplicationRolesQuery,
  useUpdateUserPermissionsMutation,
} = usersApi;

export default usersApi;
